import * as z from "zod";
import { isUniqueConstraintViolation } from "../dbErrors";
import { userFacingError } from "../authorization";
import { isPublicHttpsUrl } from "../validateExternalUrl";
import { mutator } from "./mutator";

const updateOidcProviderSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(120).optional(),
  issuer: z.string().min(1).max(2048).optional(),
  audience: z.string().min(1).max(2048).optional(),
  jwksUri: z.string().max(2048).nullable().optional(),
  disabled: z.boolean().optional(),
});

export const updateOidcProvider = mutator({
  schema: updateOidcProviderSchema,
  resolve: {
    provider: { kind: "oidcProvider", from: "id" },
  },
  permissions: [
    {
      permission: "oidc_provider:manage",
      scopeId: ({ args }) => args.provider.id,
    },
  ],
  fn: async ({
    tx,
    args: { provider, name, issuer, audience, jwksUri, disabled },
  }) => {
    if (issuer !== undefined && !isPublicHttpsUrl(issuer)) {
      throw userFacingError("Issuer must be a public HTTPS URL");
    }
    if (jwksUri != null && !isPublicHttpsUrl(jwksUri)) {
      throw userFacingError("JWKS URL must be a public HTTPS URL");
    }
    const updates: Record<string, unknown> = { updatedAt: Date.now() };
    if (name !== undefined) updates.name = name;
    if (issuer !== undefined) updates.issuer = issuer;
    if (audience !== undefined) updates.audience = audience;
    if (jwksUri !== undefined) updates.jwksUri = jwksUri;
    if (disabled !== undefined) updates.disabled = disabled;

    try {
      await tx.mutate.oidcProviders.update({ id: provider.id, ...updates });
    } catch (err) {
      if (
        isUniqueConstraintViolation(err, "oidc_providers_tenant_issuer_unique")
      ) {
        throw userFacingError(
          "A provider with this issuer is already registered"
        );
      }
      throw err;
    }
  },
});
